import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import PageForm from '../../templates/PageForm';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { IoIosRefresh } from 'react-icons/io';
import { FaFileCode, FaFileExcel, FaTimes } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import FieldsModal, { FieldItem } from './FieldsModal';
import NewTooltip from '../../atoms/NewTooltip';
import PresentationModal from '../../organisms/PresentationModal';
import CopyButton from '../../atoms/CopyButton';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import useFetch from '../../../hooks/useFetch';
import PageFooter from '../../molecules/PageFooter';
import TableSkeleton from '../../atoms/TableSkeleton';
import FormSkeleton from '../../atoms/FormSkeleton';
import Tab, { TabProps } from '../../molecules/Tab';
import CardList from '../../organisms/CardList';
import appConfig from '../../../config/app-config';
import { dataProviderApi } from '../../../services/dataProviderApi';
import PageSearch from '../../molecules/PageSearch';
import {
    BatchReportInfo,
    BatchReportItem,
    ReportItem,
    getEventColor,
    getCssColorBy,
    getContentByStatus,
    getStatusLabel,
    exportPageToCsv,
} from './batchReportUtils';
import ReportStatusBadge from './ReportStatusBadge';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const API_KEY = DATA_PROVIDER_ENV.key;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/batch-reports`;

// --- Sub-components ---

const ExportDropdown: React.FC<{ onExportAll: () => void; onExportPage: () => void; isLoading: boolean; disablePageExport?: boolean }> = ({ onExportAll, onExportPage, isLoading, disablePageExport }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div ref={ref} className="relative inline-block text-left ml-2">
            <button
                type="button"
                className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
            >
                <FaFileExcel className="mr-2" />
                {isLoading ? 'Exportando...' : 'Exportar'}
                <svg className="ml-2 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                        <button onClick={() => { setIsOpen(false); onExportAll(); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            <FaFileExcel className="mr-2 text-green-600" /> Lote completo
                        </button>
                        <button onClick={() => { setIsOpen(false); onExportPage(); }} disabled={disablePageExport} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            <FaFileCode className="mr-2 text-blue-600" /> Página atual
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm h-full">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{title}</h3>
        {children}
    </div>
);

const CountBadge: React.FC<{ count: number; label: string; colorClass: string; onClick?: () => void }> = ({ count, label, colorClass, onClick }) => (
    <div className="flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={onClick}>
        <span className={`${colorClass} text-white text-xs font-bold px-2 py-0.5 rounded`}>{count}</span>
        <span className="text-sm text-gray-600">{label}</span>
    </div>
);

// --- Main Component ---

const BatchReportPageView: React.FC = () => {
    const { id = '', page = '0', pageSize = '100', field = 'document', order = 'ASC' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const currentTab = searchParams.get('tab') || 'extraction';
    const statusFilter = searchParams.get('status') || '';

    const apiVersion = useSignal('');
    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);
    const showValueModal = useSignal(false);
    const selectedValue = useSignal<any | null>(null);
    const report = useSignal<BatchReportInfo | null>(null);
    const showFieldsModal = useSignal(false);
    const selectedFields = useSignal<FieldItem[]>([]);
    const loadingFields = useSignal(false);
    const totalPages = useSignal(0);
    const totalElements = useSignal(0);
    const refreshKey = useSignal(0);

    // Reports tab
    const reports = useSignal<ReportItem[]>([]);
    const reportsTotalPages = useSignal(0);
    const reportsTotalElements = useSignal(0);
    const [documentSearch, setDocumentSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(statusFilter);

    const currentPage = parseInt(page);
    const currentPageSize = parseInt(pageSize);
    const currentField = field;
    const currentOrder = order as 'ASC' | 'DESC';

    const handleBack = () => {
        if (location.state?.fromForm) {
            navigate('/data-provider/batch-report/0/100/createdAt/DESC');
        } else {
            navigate(-1);
        }
    };

    const exportLoading = useSignal(false);

    const handleExport = async () => {
        exportLoading.value = true;
        try {
            await dataProviderApi.downloadBatchCsv(id);
        } catch (e) {
            alert('Erro ao exportar relatório completo. Tente novamente.');
        } finally {
            exportLoading.value = false;
        }
    };

    const handleExportPage = () => {
        exportPageToCsv(rows.value, headers.value, id, currentPage);
    };

    const pageChanged = (p: number) => {
        navigate(`/data-provider/batch-report/view/${id}/${p}/${currentPageSize}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(`/data-provider/batch-report/view/${id}/0/${size}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSorted = (sortField: string, sortOrder: 'ASC' | 'DESC') => {
        navigate(`/data-provider/batch-report/view/${id}/${currentPage}/${currentPageSize}/${sortField}/${sortOrder}`, { replace: true });
    };

    const handleTabChange = (tabName: string) => {
        setSearchParams({ tab: tabName });
    };

    const handleStatusClick = (status: string) => {
        setSelectedStatus(status);
        setSearchParams({ tab: 'reports', status });
    };

    const handleReportClick = (documentRaw: string) => {
        navigate(`/data-provider/batch-report/view/${id}/report/${encodeURIComponent(documentRaw)}`);
    };

    const renderStatusTooltip = (status?: string, errorMessage?: string | null) => {
        return getContentByStatus(status, errorMessage);
    };

    // Fetch batch report data
    const pageViewParams = new URLSearchParams({ page: String(currentPage), pageSize: String(currentPageSize), order: `${currentField},${currentOrder}`, _t: String(refreshKey.value) });
    if (documentSearch) pageViewParams.set('document', documentSearch);
    if (selectedStatus) pageViewParams.set('status', selectedStatus);

    const { isLoading, data, error, isTokenError } = useFetch(
        `${BASE_URL}/${id}/page-view?${pageViewParams.toString()}`,
        [{ key: 'x-api-key', value: API_KEY }]
    );

    // Fetch reports with filters
    const reportsQueryParams = new URLSearchParams({ page: String(currentPage), pageSize: String(currentPageSize) });
    if (documentSearch) reportsQueryParams.set('document', documentSearch);
    if (selectedStatus) reportsQueryParams.set('status', selectedStatus);

    const reportsUrl = currentTab === 'reports' ? `${BASE_URL}/${id}/reports?${reportsQueryParams.toString()}` : '';
    const { isLoading: isLoadingReports, data: reportsData } = useFetch(reportsUrl, [{ key: 'x-api-key', value: API_KEY }]);

    // Fetch batch info (for provider tab)
    const batchInfoUrl = currentTab === 'provider' ? `${BASE_URL}/${id}` : '';
    const { data: batchInfoData } = useFetch(batchInfoUrl, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            totalPages.value = data.totalPages || 0;
            totalElements.value = data.totalElements || 0;

            const rpt = data.batch || data;
            if (rpt) {
                report.value = {
                    id: rpt.id,
                    provider: rpt.provider,
                    createdAt: rpt.createdAt,
                    updatedAt: rpt.updatedAt,
                    status: rpt.status,
                    documents: rpt.documentsCount || 0,
                    errors: rpt.errorCount || 0,
                    success: rpt.successCount || 0,
                    sending: rpt.sendingCount || 0,
                    statusFormatted: rpt.statusFormatted,
                    reportType: rpt.reportType,
                    batchReportId: rpt.batchReportId,
                    canUpdateStatus: rpt.canUpdateStatus,
                    mini_uuid: rpt.mini_uuid,
                    identification: rpt.identification,
                    reportTypeInfo: rpt.reportTypeInfo,
                };
            }

            const dynamicHeaders: ATableHeader[] = (data.fields || []).map((h: any, index: number) => ({
                id: h.field || `column-${index}`,
                title: h.header || `column-${index}`,
                field: h.field || `column-${index}`,
                canSort: false,
                cssClass: 'text-center',
            }));

            headers.value = [
                { id: 'document', title: 'Documento', field: 'document', canSort: true, cssClass: 'text-center', width: '150px', fixed: 'left' },
                { id: 'createdAt', title: 'Consulta em', canSort: false, cssClass: 'text-center', width: '140px' },
                ...dynamicHeaders,
                { id: 'status', title: 'Status', field: 'status', canSort: false, cssClass: 'text-center', width: '100px', fixed: 'right' },
            ];

            rows.value = data.list || data.items || data.reports || [];
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (reportsData) {
            reports.value = reportsData.reports || [];
            reportsTotalPages.value = reportsData.totalPages || 0;
            reportsTotalElements.value = reportsData.totalElements || 0;
        }
    }, [reportsData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (statusFilter) setSelectedStatus(statusFilter);
    }, [statusFilter]);

    const tabItems: TabProps[] = [
        { id: 'extraction', title: 'Extração', index: 0 },
        { id: 'reports', title: 'Relatórios', index: 1 },
        { id: 'provider', title: 'Provedor', index: 2 },
    ];

    const forceReload = () => { refreshKey.value = refreshKey.value + 1; };

    const renderValue = (value: any) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') {
            return value ? <FaCheck className="mx-auto text-green-600 text-xl" /> : <FaTimes className="mx-auto text-red-600 text-xl" />;
        }
        if (Array.isArray(value)) {
            const isPrimitive = value.every((v) => ['string', 'number', 'boolean'].includes(typeof v));
            if (isPrimitive) return value.join(', ');
            return (
                <FaFileCode className="mx-auto text-blue-600 cursor-pointer text-xl" onClick={() => { selectedValue.value = value; showValueModal.value = true; }} />
            );
        }
        if (typeof value === 'object') {
            return (
                <FaFileCode className="mx-auto text-blue-600 cursor-pointer text-xl" onClick={() => { selectedValue.value = value; showValueModal.value = true; }} />
            );
        }
        return value;
    };

    const renderRows = (items: BatchReportItem[]) => {
        if (!items || items.length === 0) {
            return (
                <tr><td colSpan={headers.value.length} className="py-2 px-2 text-center">
                    <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum registro encontrado...</div>
                </td></tr>
            );
        }
        return items.map((row, index) => (
            <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300 hover:bg-slate-300 hover:shadow-md`}>
                {headers.value.map((h) => (
                    <td key={h.id} className="py-2 px-2 text-center">
                        {h.id === 'status' ? (
                            <NewTooltip content={renderStatusTooltip(row.status, row.errorMessage)} alignment="left">
                                <span className={`rounded px-1 py-1 text-xs font-bold text-white ${getCssColorBy(row.status)}`}>
                                    {getStatusLabel(row.status)}
                                </span>
                            </NewTooltip>
                        ) : renderValue((row as any)[h.field || h.id])}
                    </td>
                ))}
            </tr>
        ));
    };

    const renderReportCard = (item: ReportItem) => (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleReportClick(item.documentRaw || item.document)}>
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <p className="font-semibold text-gray-900">{item.document}</p>
                    <ReportStatusBadge status={item.status} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-gray-500">Criado em</p><p className="text-gray-900">{item.createdAt || '-'}</p></div>
                    <div><p className="text-gray-500">Atualizado em</p><p className="text-gray-900">{item.updatedAt || '-'}</p></div>
                    <div><p className="text-gray-500">Tentativas</p><p className="text-gray-900">{item.attempts || 0} / {item.maxAttempts || 3}</p></div>
                </div>
                {item.events && item.events.length > 0 && (
                    <div className="flex gap-1 mt-2">
                        {item.events.map((event, idx) => (
                            <NewTooltip key={idx} content={<div className="text-xs"><div className="font-semibold">{event.eventName}</div><div>{event.occurredAt}</div></div>} alignment="right">
                                <div className={`w-3 h-3 rounded-sm ${getEventColor(event.eventName)} cursor-pointer`} />
                            </NewTooltip>
                        ))}
                    </div>
                )}
                {item.errorMessage && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-600">{item.errorMessage}</p>
                    </div>
                )}

            </div>
        </div>
    );

    return (
        <PageForm
            pageTitle="Relatório em lote"
            apiVersion={apiVersion.value}
            isLoading={isLoading || loadingFields.value}
            breadcrumb={[
                { label: 'Relatórios em lote', url: '/data-provider/batch-report/0/100/createdAt/DESC' },
                { label: 'Detalhe do relatório em lote', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded" onClick={handleBack}>
                        <IoMdArrowRoundBack />
                    </button>
                    {report.value?.status !== 'COMPLETED' && (
                        <button onClick={forceReload} className="p-2 ml-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Atualizar dados">
                            <IoIosRefresh size={18} />
                        </button>
                    )}
                    <ExportDropdown onExportAll={handleExport} onExportPage={handleExportPage} isLoading={exportLoading.value} disablePageExport={currentTab !== 'extraction'} />
                </div>
            }
        >
            <div className="p-2 space-y-2 overflow-hidden">
                {isLoading ? <FormSkeleton /> : report.value && (
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-7">
                            <InfoCard title="Dados do Relatório">
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-6">
                                        <span><span className="text-gray-500"># </span><span className="font-medium">{report.value.id}</span></span>
                                        <span className="flex-1"><span className="text-gray-500">Identificação: </span><span className="font-medium">{report.value.identification}</span></span>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <span><span className="text-gray-500">Provedor: </span><span className="font-medium">{report.value.provider}</span></span>
                                        <span><span className="text-gray-500">Tipo: </span><span className="font-medium">{report.value.reportType}</span></span>
                                    </div>
                                    <div className="flex items-center gap-6 pt-2 mt-2 border-t border-gray-100">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                            <span className="text-gray-500">Criado em:</span>
                                            <span className="font-medium">{report.value.createdAt}</span>
                                        </span>
                                        {report.value.updatedAt && (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                                                <span className="text-gray-500">Atualizado em:</span>
                                                <span className="font-medium">{report.value.updatedAt}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </InfoCard>
                        </div>
                        <div className="col-span-5">
                            <InfoCard title="Totais e Status">
                                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                                    <ReportStatusBadge status={report.value.status} />
                                    <span className="text-sm text-gray-600 cursor-pointer hover:underline" onClick={() => handleStatusClick('')}>Todos os documentos: <strong>{report.value.documents || 0}</strong></span>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <CountBadge count={report.value.sending || 0} label="Processando" colorClass="bg-gray-500" onClick={() => handleStatusClick('PROCESSING')} />
                                    <CountBadge count={0} label="Pendentes" colorClass="bg-yellow-500" onClick={() => handleStatusClick('PENDING')} />
                                    <CountBadge count={0} label="Aguardando" colorClass="bg-gray-400" onClick={() => handleStatusClick('WAITING')} />
                                    <CountBadge count={report.value.success || 0} label="Concluídos" colorClass="bg-blue-500" onClick={() => handleStatusClick('COMPLETED')} />
                                    <CountBadge count={report.value.errors || 0} label="Erros" colorClass="bg-red-500" onClick={() => handleStatusClick('ERROR')} />
                                    <CountBadge count={0} label="Falhou" colorClass="bg-red-800" onClick={() => handleStatusClick('FAILED')} />
                                </div>
                            </InfoCard>
                        </div>
                    </div>
                )}

                <div className="mt-4 flex items-center gap-4">
                    <Tab tabItems={tabItems} tabIndex={tabItems.findIndex(t => t.id === currentTab)} setTabIndex={(index) => handleTabChange(tabItems[index].id)} />
                    <div className="flex gap-2 ml-auto h-[46px]">
                        <PageSearch
                            value={documentSearch}
                            label="Buscar por documento..."
                            onSearch={(val) => setDocumentSearch(val)}
                            filterCleanVisible={true}
                            filterCleanEnabled={Boolean(documentSearch)}
                            onFilterClean={() => setDocumentSearch('')}
                            configureVisible={false}
                            className="w-72"
                        />
                    </div>
                </div>

                {/* Extraction Tab */}
                {currentTab === 'extraction' && (
                    <>
                        {isLoading ? <TableSkeleton rows={currentPageSize > 10 ? 10 : currentPageSize} /> : (
                            <ATable headers={headers.value} fieldSorted={currentField} orderSorted={currentOrder} onSort={pageSorted} autoResize={true} marginTop={385} enableFixedColumns={true}>
                                {renderRows(rows.value)}
                            </ATable>
                        )}
                        {!isLoading && (
                            <PageFooter currentPage={currentPage} totalPages={totalPages.value} itemsPerPage={currentPageSize} totalElements={totalElements.value} onPageChange={pageChanged} onItemsPerPageChange={pageSizeChanged} itemsLabel="registros" />
                        )}
                    </>
                )}

                {/* Reports Tab */}
                {currentTab === 'reports' && (
                    <>
                        <div className="overflow-y-auto border-b border-gray-200" style={{ height: 'calc(100vh - 385px)' }}>
                            {isLoadingReports ? <TableSkeleton rows={6} /> : (
                                <CardList items={reports.value} renderCard={renderReportCard} columns={3} emptyMessage="Nenhum relatório encontrado" />
                            )}
                        </div>
                        {!isLoadingReports && reports.value.length > 0 && (
                            <PageFooter currentPage={currentPage} totalPages={reportsTotalPages.value} itemsPerPage={currentPageSize} totalElements={reportsTotalElements.value} onPageChange={pageChanged} onItemsPerPageChange={pageSizeChanged} itemsLabel="relatórios" />
                        )}
                    </>
                )}

                {/* Provider Tab */}
                {currentTab === 'provider' && (
                    <div className="space-y-4 mt-4">
                        <InfoCard title="Configuração do Provedor">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div><span className="text-gray-500">Provedor:</span> <span className="font-medium">{report.value?.provider || '-'}</span></div>
                                <div><span className="text-gray-500">Tipo de documento:</span> <span className="font-medium">{batchInfoData?.reportTypeInfo?.documentType || '-'}</span></div>
                                <div><span className="text-gray-500">Report Type:</span> <span className="font-medium">{batchInfoData?.reportTypeInfo?.name || '-'}</span></div>
                                {batchInfoData?.reportTypeInfo?.description && (
                                    <div className="col-span-3"><span className="text-gray-500">Descrição:</span> <span className="font-medium">{batchInfoData.reportTypeInfo.description}</span></div>
                                )}
                            </div>
                        </InfoCard>
                        {batchInfoData?.fields && batchInfoData.fields.length > 0 && (
                            <InfoCard title="Campos de Extração">
                                <div className="flex flex-wrap gap-1">
                                    {batchInfoData.fields.map((f: any, idx: number) => (
                                        <span key={idx} className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded border border-blue-400">
                                            {f.header || f.field || '-'}
                                        </span>
                                    ))}
                                </div>
                            </InfoCard>
                        )}
                        {batchInfoData?.events && batchInfoData.events.length > 0 && (
                            <InfoCard title="Histórico de Eventos">
                                <div className="overflow-y-auto max-h-80">
                                    <div className="relative border-l-2 border-gray-200 ml-3">
                                        {batchInfoData.events.map((event: any, idx: number) => (
                                            <div key={idx} className="mb-4 ml-4 relative">
                                                <div className={`absolute -left-[21px] top-1.5 w-3 h-3 rounded-full ${getEventColor(event.eventName)}`} />
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{event.eventName}</p>
                                                        {event.payload && Object.keys(event.payload).length > 0 && (
                                                            <div className="mt-1 flex flex-wrap gap-1">
                                                                {Object.entries(event.payload).filter(([k]) => k !== 'batch_report_id').map(([k, v]) => (
                                                                    <span key={k} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                                                        {k}: {Array.isArray(v) ? `[${v.length} itens]` : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{event.occurredAt}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </InfoCard>
                        )}
                        {batchInfoData?.extractorMapping && Object.keys(batchInfoData.extractorMapping).length > 0 && (
                            <InfoCard title="Mapeamento do Extrator">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="border-b"><th className="text-left py-1 px-2 text-gray-500">Chave</th><th className="text-left py-1 px-2 text-gray-500">Valor</th></tr></thead>
                                        <tbody>
                                            {Object.entries(batchInfoData.extractorMapping).map(([key, val]) => (
                                                <tr key={key} className="border-b border-gray-100">
                                                    <td className="py-1 px-2 font-medium">{key}</td>
                                                    <td className="py-1 px-2">{String(val)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </InfoCard>
                        )}
                    </div>
                )}

                {/* Modals */}
                <PresentationModal id="value-modal" show={showValueModal.value} onClose={() => { showValueModal.value = false; selectedValue.value = null; }} header="Detalhes" footer={<CopyButton data={selectedValue.value} />}>
                    <Fragment>
                        <div className="p-4">
                            <div style={{ height: '500px', overflowY: 'auto' }}>
                                {selectedValue.value && <JsonView data={selectedValue.value} shouldExpandNode={allExpanded} style={defaultStyles} />}
                            </div>
                        </div>
                    </Fragment>
                </PresentationModal>
                <FieldsModal id="fields-modal" show={showFieldsModal.value} fields={selectedFields.value} onClose={() => { showFieldsModal.value = false; selectedFields.value = []; }} />
            </div>
        </PageForm>
    );
};

export default BatchReportPageView;
